import { useMemo } from 'react';
import { Space, Tag, Typography, Tooltip } from 'antd';
import { SmartDataTableProps } from '@src/components/SmartDataTable/types';
import { useStatusRender } from './useStatusRender';
import { useStockLevelRender } from './useStockLevelRender';
import {
  STATUS_FILTER_OPTIONS,
  STOCK_LEVEL_FILTER_OPTIONS,
} from '../constants';

const { Text } = Typography;

/** Expiry label in table cells (light background). */
const BATCH_EXPIRY_CELL_COLOR = 'rgba(0, 0, 0, 0.45)';
/** Expiry label inside Ant Design tooltip overlay (dark background). */
const BATCH_EXPIRY_TOOLTIP_COLOR = 'rgba(255, 255, 255, 0.88)';

export const useInventoryColumns = (): SmartDataTableProps['columns'] => {
  const renderStatus = useStatusRender();
  const renderStockLevel = useStockLevelRender();

  return useMemo(
    () => [
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id',
        sorter: true,
        minWidth: 80,
        ellipsis: true,
        render: (value: any) => (
          <Text
            style={{
              fontSize: '12px',
              color: '#8c8c8c',
              backgroundColor: '#f5f5f5',
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            #{value}
          </Text>
        ),
      },
      {
        key: 'medicine_info',
        title: 'Medicine',
        dataIndex: 'medicine',
        sorter: false,
        minWidth: 200,
        render: (medicine: any, record: any) => {
          const tooltipContent = (
            <div style={{ maxWidth: '250px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                Medicine Details
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>ID:</strong> {medicine?.id || 'N/A'}
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>Name:</strong> {medicine?.name || 'N/A'}
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>Pack Size:</strong> {medicine?.pack_size || 'N/A'}
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>HSN:</strong> {medicine?.hsn || 'N/A'}
              </div>
              <div>
                <strong>Manufacturer:</strong> {medicine?.manufacturer || 'N/A'}
              </div>
            </div>
          );

          return (
            <Tooltip title={tooltipContent} placement="topLeft">
              <div style={{ cursor: 'pointer' }}>
                <div style={{ fontWeight: 500, color: '#262626' }}>
                  {medicine?.name || '-'}
                </div>
              </div>
            </Tooltip>
          );
        },
      },
      {
        key: 'manufacturer',
        title: 'Manufacturer',
        dataIndex: 'medicine',
        sorter: false,
        minWidth: 150,
        render: (medicine: any) => (
          <Text style={{ fontWeight: 500, color: '#262626' }}>
            {medicine?.manufacturer || 'N/A'}
          </Text>
        ),
      },
      {
        key: 'current_stock',
        title: 'Current Stock',
        dataIndex: 'current_stock',
        sorter: false,
        width: 180,
        filterable: true,
        filterType: 'multiselect',
        filterOptions: [...STOCK_LEVEL_FILTER_OPTIONS],
        render: (currentStock: number, record: any) =>
          renderStockLevel(currentStock, record),
      },
      {
        key: 'manufacturer_info',
        title: 'MR/Contact Person',
        dataIndex: 'manufacturer',
        sorter: false,
        minWidth: 180,
        render: (manufacturer: any) => {
          const tooltipContent = (
            <div style={{ maxWidth: '250px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                Manufacturer Details
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>ID:</strong> {manufacturer?.id || 'N/A'}
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>Name:</strong> {manufacturer?.manufacturer || 'N/A'}
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>Phone:</strong> {manufacturer?.contact_person || 'N/A'}
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>Email:</strong> {manufacturer?.phone || 'N/A'}
              </div>
            </div>
          );

          return (
            <Tooltip title={tooltipContent} placement="topLeft">
              <div style={{ cursor: 'pointer' }}>
                <div style={{ fontWeight: 500, color: '#262626' }}>
                  {manufacturer?.contact_person || 'N/A'}
                </div>
                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                  {manufacturer?.phone || 'No phone'}
                </div>
              </div>
            </Tooltip>
          );
        },
      },
      {
        key: 'medicine_mrp',
        title: 'MRP',
        dataIndex: 'medicine',
        sorter: false,
        minWidth: 100,
        render: (medicine: any) => {
          const mrp = medicine?.mrp;
          return (
            <Text style={{ fontWeight: 500, color: '#1890ff' }}>
              ₹
              {mrp
                ? Number(mrp).toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                  })
                : '0.00'}
            </Text>
          );
        },
      },
      {
        key: 'purchase_price',
        title: 'Rate',
        dataIndex: 'purchase_price',
        sorter: true,
        minWidth: 120,
        render: (price: string) => (
          <Text style={{ fontWeight: 500, color: '#52c41a' }}>
            ₹
            {price
              ? Number(price).toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                })
              : '0.00'}
          </Text>
        ),
      },
      {
        key: 'batches',
        title: 'Batches',
        dataIndex: 'batches',
        sorter: false,
        minWidth: 200,
        filterable: true,
        filterType: 'daterange',
        filterField: 'batches.expiry_date',
        render: (batches: any[] | undefined) => {
          if (!batches?.length) {
            return <Text type="secondary">—</Text>;
          }

          const renderBatchRows = (
            list: any[],
            expiryColor: string,
            tagInTooltip?: boolean
          ) => (
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              {list.map((b: any) => (
                <div
                  key={b.id ?? `${b.batch_number}-${b.expiry_date}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 8,
                  }}
                >
                  <Tag
                    style={
                      tagInTooltip
                        ? {
                            marginInlineEnd: 0,
                            background: 'rgba(255, 255, 255, 0.12)',
                            borderColor: 'rgba(255, 255, 255, 0.25)',
                            color: 'rgba(255, 255, 255, 0.95)',
                          }
                        : { marginInlineEnd: 0 }
                    }
                  >
                    {b.batch_number || '—'}
                  </Tag>
                  <span style={{ fontSize: 12, color: expiryColor }}>
                    Exp: {b.expiry_date ?? '—'}
                  </span>
                </div>
              ))}
            </Space>
          );

          if (batches.length <= 3) {
            return renderBatchRows(batches, BATCH_EXPIRY_CELL_COLOR, false);
          }

          const tooltipTitle = renderBatchRows(
            batches,
            BATCH_EXPIRY_TOOLTIP_COLOR,
            true
          );

          return (
            <Tooltip
              title={tooltipTitle}
              placement="topLeft"
              overlayStyle={{ maxWidth: 320 }}
            >
              <div style={{ cursor: 'pointer' }}>
                <Space direction="vertical" size={6}>
                  {batches.slice(0, 2).map((b: any) => (
                    <div
                      key={b.id ?? `${b.batch_number}-${b.expiry_date}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 8,
                      }}
                    >
                      <Tag style={{ marginInlineEnd: 0 }}>
                        {b.batch_number || '—'}
                      </Tag>
                      <span
                        style={{ fontSize: 12, color: BATCH_EXPIRY_CELL_COLOR }}
                      >
                        Exp: {b.expiry_date ?? '—'}
                      </span>
                    </div>
                  ))}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    +{batches.length - 2} more (hover)
                  </Text>
                </Space>
              </div>
            </Tooltip>
          );
        },
      },
      {
        key: 'status',
        title: 'Status',
        dataIndex: 'status',
        sorter: true,
        minWidth: 120,
        filterable: true,
        filterType: 'multiselect',
        filterOptions: [...STATUS_FILTER_OPTIONS],
        render: renderStatus,
      },
    ],
    [renderStatus, renderStockLevel]
  );
};
